import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Package, DollarSign, TrendingUp, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    pendingOrders: 0
  });

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/orders`);
      const ordersData = response.data;
      setOrders(ordersData);

      // Calculate stats
      setStats({
        totalOrders: ordersData.length,
        totalRevenue: ordersData.reduce((sum, order) => sum + order.total, 0),
        pendingOrders: ordersData.filter(order => order.status === 'pending').length
      });
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.patch(`${API}/orders/${orderId}/status?status=${newStatus}`);
      toast.success('Order status updated');
      fetchOrders();
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Failed to update order status');
    }
  };

  return (
    <div className="bg-off_white min-h-screen py-12" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-12">
          <h1 className="font-chewy text-5xl sm:text-7xl">
            <span className="text-brand_blue">ADMIN</span>{' '}
            <span className="text-brand_red">DASHBOARD</span>
          </h1>
          <button
            onClick={fetchOrders}
            className="bg-brand_blue text-white font-nunito font-bold px-6 py-3 rounded-full border-2 border-black shadow-hard hover:shadow-hard-hover transition-all"
            data-testid="refresh-button"
          >
            <RefreshCw className="inline-block w-5 h-5 mr-2" strokeWidth={3} />
            Refresh
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white border-4 border-black rounded-xl p-6 shadow-hard" data-testid="stat-total-orders">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-nunito text-sm font-bold text-gray-600 mb-1">Total Orders</p>
                <p className="font-chewy text-4xl text-brand_blue">{stats.totalOrders}</p>
              </div>
              <div className="bg-brand_blue/20 p-4 rounded-full border-2 border-black">
                <Package className="w-8 h-8 text-brand_blue" strokeWidth={3} />
              </div>
            </div>
          </div>

          <div className="bg-white border-4 border-black rounded-xl p-6 shadow-hard" data-testid="stat-revenue">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-nunito text-sm font-bold text-gray-600 mb-1">Total Revenue</p>
                <p className="font-chewy text-4xl text-brand_green">${stats.totalRevenue.toFixed(2)}</p>
              </div>
              <div className="bg-brand_green/20 p-4 rounded-full border-2 border-black">
                <DollarSign className="w-8 h-8 text-brand_green" strokeWidth={3} />
              </div>
            </div>
          </div>

          <div className="bg-white border-4 border-black rounded-xl p-6 shadow-hard" data-testid="stat-pending">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-nunito text-sm font-bold text-gray-600 mb-1">Pending Orders</p>
                <p className="font-chewy text-4xl text-brand_red">{stats.pendingOrders}</p>
              </div>
              <div className="bg-brand_red/20 p-4 rounded-full border-2 border-black">
                <TrendingUp className="w-8 h-8 text-brand_red" strokeWidth={3} />
              </div>
            </div>
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white border-4 border-black rounded-xl p-6 shadow-hard">
          <h2 className="font-chewy text-3xl text-brand_blue mb-6">Recent Orders</h2>
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-16 h-16 border-4 border-brand_blue border-t-transparent rounded-full animate-spin" />
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12">
              <p className="font-nunito text-xl text-gray-600 font-semibold">No orders yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-black">
                    <th className="text-left font-nunito font-bold py-3 px-2">Order ID</th>
                    <th className="text-left font-nunito font-bold py-3 px-2">Customer</th>
                    <th className="text-left font-nunito font-bold py-3 px-2">Date</th>
                    <th className="text-left font-nunito font-bold py-3 px-2">Total</th>
                    <th className="text-left font-nunito font-bold py-3 px-2">Payment</th>
                    <th className="text-left font-nunito font-bold py-3 px-2">Status</th>
                    <th className="text-left font-nunito font-bold py-3 px-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr
                      key={order.id}
                      className="border-b border-gray-200 hover:bg-off_white"
                      data-testid={`admin-order-${order.id}`}
                    >
                      <td className="py-4 px-2 font-nunito font-semibold">
                        #{order.id.slice(0, 8).toUpperCase()}
                      </td>
                      <td className="py-4 px-2">
                        <div className="font-nunito">
                          <div className="font-semibold">{order.customer_name}</div>
                          <div className="text-sm text-gray-600">{order.customer_email}</div>
                        </div>
                      </td>
                      <td className="py-4 px-2 font-nunito text-sm">
                        {new Date(order.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-4 px-2 font-nunito font-bold">
                        ${order.total.toFixed(2)}
                      </td>
                      <td className="py-4 px-2">
                        <span
                          className={`font-nunito text-xs font-bold px-3 py-1 rounded-full border-2 border-black capitalize ${
                            order.payment_status === 'paid'
                              ? 'bg-brand_green/20 text-brand_green'
                              : 'bg-brand_yellow/20 text-black'
                          }`}
                        >
                          {order.payment_status}
                        </span>
                      </td>
                      <td className="py-4 px-2">
                        <select
                          value={order.status}
                          onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                          className="font-nunito font-bold px-3 py-1 rounded-lg border-2 border-black capitalize bg-white"
                          data-testid={`status-select-${order.id}`}
                        >
                          <option value="pending">Pending</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="processing">Processing</option>
                          <option value="shipped">Shipped</option>
                          <option value="delivered">Delivered</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </td>
                      <td className="py-4 px-2">
                        <button
                          onClick={() => {
                            const details = `
Order: #${order.id.slice(0, 8).toUpperCase()}
Customer: ${order.customer_name}
Email: ${order.customer_email}
Phone: ${order.customer_phone || 'N/A'}
Shipping: ${order.shipping_method}
Address: ${order.shipping_address || 'N/A'}

Items:
${order.items.map(item => `- ${item.name} x${item.quantity} ($${(item.price * item.quantity).toFixed(2)})`).join('\n')}

Subtotal: $${order.subtotal.toFixed(2)}
Shipping: $${order.shipping_cost.toFixed(2)}
Total: $${order.total.toFixed(2)}
                            `;
                            alert(details);
                          }}
                          className="bg-brand_blue text-white px-3 py-1 rounded-full font-nunito text-sm font-bold border-2 border-black shadow-hard hover:shadow-hard-hover transition-all"
                          data-testid={`view-details-${order.id}`}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Admin;